/**
 * Which SPA surface a stored token belongs on: the admin console, or not.
 *
 * WHY THIS EXISTS
 * ---------------
 * `authSlice.isAuthenticated` is `!!localStorage.admin_token` and nothing
 * more. That key is not only written by the admin JWT form: the workspace
 * settings session bridge writes an ORDINARY user's workspace access token to
 * the same key. So "a token is present" never meant "this is an admin", and
 * routing on it alone sent normal users into the admin console (or, with no
 * token at all, onto the paste-a-JWT page).
 *
 * This mirrors `backend/src/middleware/admin.ts` `requireAdmin`, the gate on
 * every `/api/admin/*` call the console makes. A token that fails it here
 * would fail every console request with 403, so rendering the console for it
 * only strands the user.
 *
 * NOT A SECURITY BOUNDARY. The signature is not (and cannot be) verified in
 * the browser; the server remains the only authority. This decides which page
 * to render, and fails closed: anything unparseable is "not admin", which
 * routes to the ordinary user area, never to the admin console.
 */

/** Explicit, admin-only entry to the JWT-paste console login. */
export const ADMIN_LOGIN_PATH = '/admin/login';

/** The admin console's root (Overview). Renders only for admin-claimed tokens. */
export const ADMIN_CONSOLE_PATH = '/';

/** The ordinary signed-in user's landing page (session-authenticated). */
export const USER_HOME_PATH = '/home';

interface AdminClaims {
  centralAdmin?: unknown;
  isAdmin?: unknown;
  roles?: unknown;
}

function decodeJwtPayload(token: string): AdminClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as AdminClaims) : null;
  } catch {
    return null;
  }
}

/**
 * The token's subject (the user id), for UI decisions only — never for
 * authorization, which Central makes on the server. Null when unreadable.
 */
export function tokenSubject(token: string | null | undefined): string | null {
  if (!token) return null;
  const claims = decodeJwtPayload(token) as (AdminClaims & { sub?: unknown }) | null;
  return claims && typeof claims.sub === 'string' && claims.sub.length > 0 ? claims.sub : null;
}

/**
 * True when the token carries one of the claims `requireAdmin` accepts:
 * `centralAdmin: true` (canonical), `isAdmin: true` (legacy), or
 * `roles` containing `central_admin` (legacy).
 */
export function hasCentralAdminClaims(token: string | null | undefined): boolean {
  if (!token) return false;
  const claims = decodeJwtPayload(token);
  if (!claims) return false;
  if (claims.centralAdmin === true) return true;
  if (claims.isAdmin === true) return true;
  return Array.isArray(claims.roles) && claims.roles.includes('central_admin');
}
